<script setup lang="ts">
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { WorkRecord } from '~/types/datocms'
import { theme } from '#tailwind-config'

interface DetailNextProps {
  allRecordsCount: number
  allRecords: WorkRecord[]
  currentRecordSlug: string
  type?: 'work'
}

const props = withDefaults(defineProps<DetailNextProps>(), {
  type: 'work'
})

const currentRecordIndex = props.allRecords.findIndex(
  (record: WorkRecord) => record.slug === props.currentRecordSlug
)

const nextRecord: WorkRecord =
  currentRecordIndex === props.allRecordsCount - 1
    ? props.allRecords[0]
    : props.allRecords[currentRecordIndex + 1]

const breakpoints = useBreakpoints(theme.screens)
const isScreenLg = breakpoints.greater('lg')
let tl: GSAPTimeline | null = null
const ctxRef = ref<HTMLElement | null>(null)
let ctx: gsap.Context | null = null
const isTitleAnimating = ref(false)

onMounted(() => {
  if (ctxRef.value) {
    gsap.registerPlugin(ScrollTrigger)

    const mm = gsap.matchMedia()
    mm.add('(min-width: 1024px)', () => {
      ctx = gsap.context((self: gsap.Context) => {
        const elLabel = self.selector?.('.fx-label')
        const elGradient = self.selector('.fx-g')
        // const elCta = self.selector('.fx-cta')

        if (elLabel) {
          gsap.set(elLabel, { y: '100%' })
          // gsap.set(elImage, { clipPath: 'polygon(0 100%, 0 100%, 0 0, 0 0)', autoAlpha: 0 })
          gsap.set(elGradient, {
            x: '-50%'
          })
          // gsap.set(elCta, { autoAlpha: 0 })

          tl = gsap.timeline({
            scrollTrigger: {
              trigger: ctxRef.value,
              start: 'top center-=50px'
            }
          })
          tl.addLabel('start')
          tl.to(
            elGradient,
            {
              duration: 1.5,
              x: '0%',
              ease: 'power4.out'
            },
            'start'
          )
          tl.to(
            elLabel,
            {
              duration: 0.8,
              y: '0%',
              onComplete: () => {
                isTitleAnimating.value = true
              }
            },
            'start+=0.4'
          )
          /*
          tl.to(
            elCta,
            {
              duration: 0.8,
              autoAlpha: 1,

              ease: 'power2.out'
            },
            'start'
          )
   
          tl.to(
            elImage,
            {
              clipPath: 'polygon(0 100%, 100% 100%, 100% 0, 0 0)',
              duration: 1,
              autoAlpha: 1,
              ease: 'expo.out'
            },
            'start'
          )
          */
        }
      }, ctxRef.value as HTMLElement)
    })
    useLenisSpeed()
  }
})

onUnmounted(() => {
  if (ctx) {
    ctx.revert()
  }
})
</script>

<template>
  <div ref="ctxRef" class="relative flex max-w-[85%] flex-col gap-3 lg:gap-0">
    <div class="order-2 lg:order-1">
      <NuxtLink
        :to="usePathByType(nextRecord._modelApiKey, nextRecord.slug as string)"
        class="relative block h-36 w-full overflow-hidden rounded-r-full lg:h-[22.625rem]"
      >
        <Image
          :data-speed="1.5"
          class="fx-image absolute bottom-0 left-0 h-[200%] w-full object-cover"
          :src="nextRecord.image?.url"
          :srcset="
            nextRecord.image?.responsiveImage
              ? nextRecord.image?.responsiveImage?.webpSrcSet
              : undefined
          "
          :width="nextRecord.image?.width ?? 400"
          :height="nextRecord.image?.height ?? 300"
          :alt="nextRecord.image?.alt ?? ''"
        />
        <div class="fx-g absolute left-0 top-0 h-full w-[200%]" />
      </NuxtLink>
      <ClientOnly>
        <ButtonGlass
          variant="arrow"
          :to="usePathByType(nextRecord._modelApiKey, nextRecord.slug as string)"
          :size="isScreenLg ? 'lg' : 'md'"
          class="fx-cta !absolute -bottom-10 -right-14 lg:-bottom-13 lg:-right-28"
        />
      </ClientOnly>
    </div>
    <div
      class="pointer-events-none relative z-10 order-1 flex max-w-80 flex-col justify-end px-4 lg:absolute lg:left-0 lg:top-0 lg:order-2 lg:h-full lg:max-w-3xl lg:gap-1 lg:px-10 lg:pb-5 xl:max-w-4xl"
    >
      <div class="overflow-hidden text-sm font-bold lg:text-2xl lg:uppercase">
        <span class="fx-label inline-block">Next</span>
      </div>
      <div
        class="fx-next-title text-2xl font-black uppercase leading-none lg:text-6xl lg:leading-[0.88]"
      >
        <TextReveal :delay="0.3">{{ nextRecord.title }}</TextReveal>
      </div>
    </div>
  </div>
</template>

<style lang="postcss">
.fx-g {
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 1) 0%,
    rgba(255, 255, 255, 1) 10%,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 0) 50%,
    rgba(255, 255, 255, 1) 50%,
    rgba(255, 255, 255, 1) 100%
  );
}
</style>
